import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { AgenticButton } from '@/components/ui/AgenticButton';
import Icon from '../AppIcon';
import { useTheme } from '../../context/ThemeContext';
import { Sun, Moon, LogOut, User as UserIcon, ChevronDown } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useAIAgent } from '../../contexts/AIAgentContext';
import { Button } from './Button';


const Header = () => {
  const { user, signOut } = useAuth();
  const { activateAgent, isActive } = useAIAgent();
  const navigate = useNavigate();
  const [isSearchExpanded, setIsSearchExpanded] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  // Agent Invite Code State
  const [isInviteModalOpen, setIsInviteModalOpen] = useState(false);
  const [inviteCode, setInviteCode] = useState('');
  const [inviteError, setInviteError] = useState('');

  const handleAgentClick = () => {
    setIsInviteModalOpen(true);
    setInviteCode('');
    setInviteError('');
  };

  const handleInviteSubmit = (e) => {
    e.preventDefault();
    if (inviteCode === '1212') {
      setIsInviteModalOpen(false);
      activateAgent();
    } else {
      setInviteError('Invalid invite code. Please try again.');
    }
  };

  // Derived state from Supabase Auth
  const isAuthenticated = !!user;

  const { theme, toggleTheme } = useTheme();

  const handleSignOut = async () => {
    await signOut();
    navigate('/');
  };

  const searchRef = useRef(null);
  const userMenuRef = useRef(null);
  const location = useLocation();

  const navigationItems = {
    'job-seeker': [
      { label: 'Dashboard', path: '/job-seeker-dashboard', icon: 'LayoutDashboard' },
    ],
    'recruiter': [
      { label: 'Dashboard', path: '/recruiter-dashboard-analytics', icon: 'BarChart3' },
      { label: 'Post Job', path: '/job-posting-creation-management', icon: 'Plus' },
      { label: 'Company', path: '/company-registration-profile-setup', icon: 'Building2' },
    ],
    'admin': [
      { label: 'Admin Panel', path: '/admin-moderation-management', icon: 'Shield' },
      { label: 'Analytics', path: '/recruiter-dashboard-analytics', icon: 'BarChart3' },
    ],
    'anonymous': [
    ]
  };

  const validRoles = Object.keys(navigationItems);
  const rawRole = user?.user_metadata?.role || 'job-seeker';
  // Ensure the role exists in our navigation map, otherwise default to job-seeker
  const userRole = validRoles.includes(rawRole) ? rawRole : 'job-seeker';

  const currentNavItems = isAuthenticated ? navigationItems?.[userRole] : navigationItems?.['anonymous'];

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef?.current && !searchRef?.current?.contains(event?.target)) {
        setIsSearchExpanded(false);
      }
      if (userMenuRef?.current && !userMenuRef?.current?.contains(event?.target)) {
        setIsUserMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSearchSubmit = (e) => {
    e?.preventDefault();
    if (searchQuery?.trim()) {
      navigate(`/ job - search - browse ? q = ${encodeURIComponent(searchQuery.trim())} `);
      setIsSearchExpanded(false); // Close mobile search if open
    }
  };

  const handleSearchChange = (e) => {
    setSearchQuery(e?.target?.value);
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const toggleUserMenu = () => {
    setIsUserMenuOpen(!isUserMenuOpen);
  };

  const isActivePath = (path) => {
    return location?.pathname === path;
  };

  const getUserDisplayName = () => {
    const meta = user?.user_metadata;
    if (meta?.first_name || meta?.last_name) {
      return `${meta.first_name || ''} ${meta.last_name || ''} `.trim();
    }
    return meta?.full_name || user?.email?.split('@')[0] || 'User';
  };

  const getUserMenuItems = () => {
    return [];
  };

  return (
    <header className="sticky top-0 z-1040 bg-white dark:bg-black border-b border-gray-200 dark:border-border shadow-soft transition-colors duration-300">
      <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20 md:h-24 flex-nowrap">
          <Link to="/" className="flex items-center gap-0 group shrink-0">
            <span className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white group-hover:text-primary-600 dark:group-hover:text-primary-400 transition-smooth tracking-tighter relative z-10">
              Grad
            </span>
            <div className="w-9 h-9 md:w-12 md:h-12 flex items-center justify-center -mx-1 md:-mx-1">
              <img
                src="/assets/images/logo-o.svg"
                alt="GradOne logo"
                className="w-full h-full object-contain animate-spin-slow drop-shadow-sm"
              />
            </div>
            <span className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white group-hover:text-primary-600 dark:group-hover:text-primary-400 transition-smooth tracking-tighter relative z-10">
              ne
            </span>
          </Link>

          {/* Search Bar - Desktop */}
          <div className="hidden md:flex flex-1 max-w-2xl mx-8" ref={searchRef}>
            <form onSubmit={handleSearchSubmit} className="w-full">
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Icon name="Search" size={20} className="text-gray-400 dark:text-slate-400" />
                </div>
                <input
                  type="text"
                  value={searchQuery}
                  onChange={handleSearchChange}
                  placeholder="Search jobs, companies, or skills..."
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-smooth placeholder-gray-500 dark:placeholder-gray-400"
                />
                {searchQuery && (
                  <button
                    type="button"
                    onClick={() => setSearchQuery('')}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  >
                    <Icon name="X" size={16} className="text-gray-400 dark:text-slate-400" />
                  </button>
                )}
              </div>
            </form>
          </div>



          {/* User Menu & Mobile Controls */}
          <div className="flex items-center space-x-2 shrink-0">

            {/* Desktop Navigation moved here to be closer to user menu */}
            <nav className="hidden md:flex items-center space-x-1 mr-2">
              {currentNavItems?.map((item) => (
                <Link
                  key={item?.path}
                  to={item?.path}
                  className={`flex items-center space-x-2 px-3 py-2 rounded-md text-sm font-medium transition-smooth min-h-touch ${isActivePath(item?.path)
                    ? 'bg-primary-600 text-primary-foreground' : 'text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-800'
                    } `}
                >
                  <Icon name={item?.icon} size={16} />
                  <span>{item?.label}</span>
                </Link>
              ))}
            </nav>



            {!isActive && isAuthenticated && (
              <div className="hidden md:block mr-2">
                <div className="hidden md:block mr-2">
                  <AgenticButton onClick={handleAgentClick} />
                </div>
              </div>
            )}

            {/* Theme Toggle Button */}
            <button
              onClick={toggleTheme}
              className="p-2 rounded-md text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 focus:outline-none transition-colors duration-200"
              aria-label="Toggle theme"
            >
              {theme === 'dark' ? (
                <Sun size={20} className="text-yellow-400" />
              ) : (
                <Moon size={20} className="text-slate-600" />
              )}
            </button>

            {/* Search Icon - Mobile */}
            <button
              onClick={() => setIsSearchExpanded(!isSearchExpanded)}
              className="md:hidden p-2 rounded-md text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-800 transition-smooth min-h-touch min-w-touch"
            >
              <Icon name="Search" size={20} />
            </button>

            {/* User Menu / Sign In */}
            {isAuthenticated ? (
              <div className="relative" ref={userMenuRef}>
                <button
                  onClick={toggleUserMenu}
                  className="flex items-center space-x-2 p-2 rounded-md text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-800 transition-smooth min-h-touch"
                >
                  <div className="w-8 h-8 bg-black dark:bg-white rounded-full flex items-center justify-center border-2 border-primary/20">
                    {user?.user_metadata?.avatar_url ? (
                      <img src={user.user_metadata.avatar_url} alt="Profile" className="w-full h-full object-cover rounded-full" />
                    ) : (
                      <UserIcon size={16} className="text-primary-foreground dark:text-primary" />
                    )}
                  </div>
                  <span className="hidden lg:block text-sm font-bold text-gray-900 dark:text-white">{getUserDisplayName()}</span>
                  <ChevronDown size={16} className={`text - gray - 400 transition - transform duration - 200 ${isUserMenuOpen ? 'rotate-180' : ''} `} />
                </button>

                {/* User Dropdown */}
                {isUserMenuOpen && (
                  <div className="absolute right-0 mt-2 w-56 bg-white dark:bg-neutral-900 rounded-xl shadow-2xl border border-neutral-200 dark:border-neutral-800 z-1050 transition-colors duration-200 overflow-hidden">
                    <div className="py-2" role="menu" aria-orientation="vertical" aria-labelledby="user-menu">
                      <div className="px-4 py-2 border-b border-neutral-100 dark:border-neutral-800 mb-1">
                        <p className="text-sm font-bold text-neutral-900 dark:text-white truncate">{getUserDisplayName()}</p>
                        <p className="text-xs text-neutral-500 truncate">{user.email}</p>
                      </div>

                      {getUserMenuItems().map((item, index) => (
                        <button
                          key={index}
                          onClick={() => {
                            if (item.path !== '#') navigate(item.path);
                            setIsUserMenuOpen(false);
                          }}
                          className="flex w-full items-center px-4 py-2.5 text-sm text-neutral-600 dark:text-neutral-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                          role="menuitem"
                        >
                          <Icon name={item.icon} size={18} className="mr-3 text-neutral-400" />
                          {item.label}
                        </button>
                      ))}

                      <button
                        onClick={handleSignOut}
                        className="flex w-full items-center px-4 py-3 text-sm font-bold text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors border-t border-neutral-100 dark:border-neutral-800 mt-1"
                        role="menuitem"
                      >
                        <LogOut className="mr-3 h-5 w-5" />
                        Sign out
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <Button
                onClick={() => navigate('/job-seeker-registration-login')}
                className="rounded-full px-6 h-10 font-bold"
              >
                Sign In
              </Button>
            )}

          </div>
        </div>

        {/* Mobile Search Bar */}
        {isSearchExpanded && (
          <div className="md:hidden pb-4" ref={searchRef}>
            <form onSubmit={handleSearchSubmit}>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Icon name="Search" size={20} className="text-gray-400 dark:text-slate-400" />
                </div>
                <input
                  type="text"
                  value={searchQuery}
                  onChange={handleSearchChange}
                  placeholder="Search jobs, companies, or skills..."
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-smooth placeholder-gray-500 dark:placeholder-gray-400"
                  autoFocus
                />
                {searchQuery && (
                  <button
                    type="button"
                    onClick={() => setSearchQuery('')}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  >
                    <Icon name="X" size={16} className="text-gray-400 dark:text-slate-400" />
                  </button>
                )}
              </div>
            </form>
          </div>
        )}

        {/* Mobile Navigation Menu */}
        {isMobileMenuOpen && (
          <div className="md:hidden border-t border-gray-200 dark:border-gray-700">
            <nav className="py-4 space-y-1">
              {currentNavItems?.map((item) => (
                <Link
                  key={item?.path}
                  to={item?.path}
                  onClick={() => setIsMobileMenuOpen(false)}
                  className={`flex items - center space - x - 3 px - 4 py - 3 rounded - md text - base font - medium transition - smooth min - h - touch ${isActivePath(item?.path)
                    ? 'bg-primary-600 text-primary-foreground' : 'text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-800'
                    } `}
                >
                  <Icon name={item?.icon} size={20} />
                  <span>{item?.label}</span>
                </Link>
              ))}
            </nav>
          </div>
        )}
      </div>

      {/* Invite Code Modal */}
      <AnimatePresence>
        {isInviteModalOpen && (
          <div className="fixed inset-0 z-1050 flex items-center justify-center p-4">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setIsInviteModalOpen(false)}
              className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className="relative w-full max-w-sm bg-white dark:bg-neutral-900 rounded-2xl shadow-2xl overflow-hidden border border-neutral-200 dark:border-neutral-800"
            >
              <div className="p-6">
                <div className="flex justify-center mb-4">
                  <div className="w-12 h-12 rounded-full bg-violet-100 dark:bg-violet-900/30 flex items-center justify-center text-violet-600 dark:text-violet-400">
                    <Icon name="Lock" size={24} />
                  </div>
                </div>

                <h3 className="text-xl font-bold text-center text-neutral-900 dark:text-white mb-2">
                  Enter Invite Code
                </h3>
                <p className="text-sm text-center text-neutral-500 dark:text-neutral-400 mb-6">
                  Agentic Mode is currently in private beta. Please enter your invite code to access.
                </p>

                <form onSubmit={handleInviteSubmit} className="space-y-4">
                  <div>
                    <input
                      type="password"
                      value={inviteCode}
                      onChange={(e) => {
                        setInviteCode(e.target.value);
                        setInviteError('');
                      }}
                      placeholder="Enter code (e.g. 1234)"
                      className="w-full px-4 py-3 text-center text-lg tracking-widest rounded-xl border border-neutral-200 dark:border-neutral-800 bg-neutral-50 dark:bg-neutral-900 focus:ring-2 focus:ring-violet-500 outline-none transition-all"
                      autoFocus
                    />
                    {inviteError && (
                      <p className="text-red-500 text-xs text-center mt-2 animate-in fade-in slide-in-from-top-1">
                        {inviteError}
                      </p>
                    )}
                  </div>

                  <button
                    type="submit"
                    className="w-full py-3 px-4 bg-violet-600 hover:bg-violet-700 text-white font-medium rounded-xl transition-colors flex items-center justify-center gap-2"
                  >
                    <span>Unlock Access</span>
                    <Icon name="ArrowRight" size={16} />
                  </button>
                </form>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </header >
  );
};

export default Header;